import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import get from 'lodash.get';
import i18next from 'i18next';

import ArticleDek from 'components/Article/ArticleDek';
import ArticleHeroHeadline from 'components/Article/ArticleHero/ArticleHeroHeadline';
import Caption from 'components/Caption';
import ContentTimestamp from 'components/ContentTimestamp';
import InlineVideo from 'components/InlineVideo';
import Picture from 'components/Picture';
import PlaymakerPlayer from 'components/PlaymakerPlayer';
import Sponsored from 'components/Sponsored';
import TodayLogo from 'components/TodayLogo';
import Unibrow from 'components/Unibrow';
import VideoSlate from 'components/VideoPlayer/VideoSlate';
import RecipeInfo from 'components/Recipe/RecipeInfo';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import {
  video as videoPropType,
  socialMediaProfiles as socialPropType,
} from 'lib/CustomPropTypes';
import { AUTOPLAY_ARTICLE_HERO_VIDEOS } from 'lib/brandFeatures.json';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { getPIDforPlayerKey, NEWS_NOW_DEFAULT_SLATE } from 'lib/playmaker';
import { imageAltForImage } from 'lib/imageAlt';
import { sectionContainSlug } from 'lib/taxonomy';

import { isMSNBCDaily } from '../articleUtils';

const block = 'article-hero';

const mapStateToProps = ({ shared }) => ({
  socialMediaProfiles: shared.socialMediaProfiles,
});

class ArticleHero extends Component {
  static propTypes = {
    // this needs a new custom proptype called primaryMedia that can accept image/vid or null
    content: videoPropType.isRequired,
    headlineClassName: PropTypes.arrayOf(PropTypes.string),
    isLiveBlog: PropTypes.bool,
    isRecipe: PropTypes.bool,
    path: PropTypes.string,
    socialMediaProfiles: socialPropType.isRequired,
    stylizedHeader: PropTypes.node,
    stylizedTag: PropTypes.node,
    vertical: PropTypes.string.isRequired,
    isChromeless: PropTypes.bool,
    loadingLazy: PropTypes.bool,
  };

  static childContextTypes = {
    setStickyVideoFlag: PropTypes.func,
  };

  static defaultProps = {
    headlineClassName: [],
    isLiveBlog: false,
    path: '',
    stylizedHeader: null,
    stylizedTag: null,
    isChromeless: false,
    loadingLazy: false,
    isRecipe: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      hasStickyVideo: false,
      isVideoPlaying: false,
    };
  }

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  }

  getChildContext = () => ({
    setStickyVideoFlag: this.setStickyVideoFlag,
  });

  getMediaPresentationStyle() {
    const { isRecipe } = this.props;

    const style = get(this.props, 'content.primaryMedia.presentation.style');
    const type = get(this.props, 'content.primaryMedia.type');

    if (isRecipe) {
      return 'default';
    }
    if (type && ['embeddedPlaymakerLiveVideo', 'embeddedVideo'].includes(type)) {
      return 'default';
    }
    if (style && ['default', 'spread'].includes(style)) {
      return style;
    }
    return 'none';
  }

  getMedia = (loadingLazy) => {
    const { isRecipe, content } = this.props;
    const type = get(this.props, 'content.primaryMedia.type');

    // recipes will only be images and they do not have a primaryMedia key for now
    if (isRecipe && content.primaryImage) {
      return this.renderImage();
    }

    switch (type) {
      case 'embeddedVideo':
        return this.renderVideo(loadingLazy);
      case 'embeddedPlaymakerLiveVideo':
        return this.renderLiveVideo(loadingLazy);
      case 'embeddedImage':
        return this.renderImage(loadingLazy);
      default:
        return null;
    }
  }

  checkABTestingFeature = (feature) => {
    const { autoplayTestGroup } = this.state;
    return autoplayTestGroup === feature;
  }

  renderFeaturedAuthor = () => {
    // featured author is a property on the first author of the authors array
    const { content: { authors }, isRecipe } = this.props;
    if (isRecipe) return null;

    if (!Array.isArray(authors) || authors.length === 0) {
      return null;
    }

    const { featuredAuthor: { isFeaturedAuthor }, person: { name } } = authors[0];
    if (!isFeaturedAuthor) {
      return null;
    }

    return (
      <span className="article-hero__featured">
        { name }
        &nbsp;
      </span>
    );
  }

  renderTimestamp = () => {
    // timestamps get rendered in the Hero on recipes only
    const { content, isRecipe } = this.props;
    const { source, dateCreated, datePublished } = content;

    if (!isRecipe) {
      return null;
    }

    return (
      <div className={classNames(
        'founders-mono mt2 mt3-l f2 lh-copy gray-80 ls-tight',
        {
          'dn-print': isRecipe,
        },
      )}
      >
        <ContentTimestamp
          dateOnly
          dateCreated={dateCreated}
          datePublished={datePublished}
          delimiter=", "
          meta
          showCreatedDate
        />
        {source && source.name && (
          <span className="article-body__source">
            {(dateCreated || datePublished) ? ' / ' : ''}
            {i18next.t('Source')}
            {': '}
            {source.name}
          </span>
        )}
      </div>
    );
  }

  renderDek = () => {
    const { content: { dek, taxonomy: { sections }, isRecipe } } = this.props;

    if (!dek || isRecipe) {
      return null;
    }

    const isShoppingSection = sectionContainSlug(sections, 'shopping');
    const mediaPresentationStyle = this.getMediaPresentationStyle();

    return (
      <ArticleDek
        dek={dek}
        isShoppingSection={isShoppingSection}
        hasNoImage={mediaPresentationStyle === 'none'}
      />
    );
  }

  renderImage = () => {
    const { content: { primaryMedia, primaryImage }, isRecipe } = this.props;
    const mediaToUse = (isRecipe ? primaryImage : primaryMedia.image) || {};

    const { width, height } = mediaToUse;
    const url = mediaToUse.url.primary;

    return (
      <figure className="article-hero__main">
        <Picture
          loadingLazy={false}
          url={url}
          className="article-hero__main-image"
          alt={imageAltForImage(mediaToUse)}
          flavor="fit"
          responsiveFlavors={{
            s: AIMS_FLAVORS.FIT_760w,
            m: AIMS_FLAVORS.FIT_1000w,
            l: AIMS_FLAVORS.FIT_1240w,
            xl: AIMS_FLAVORS.FIT_2000w,
          }}
          originalHeight={height}
          originalWidth={width}
        />
      </figure>
    );
  }

  renderVideo = () => {
    const {
      vertical, content: { primaryMedia }, socialMediaProfiles, isChromeless,
    } = this.props;
    const autoPlayConfig = getFeatureConfigForBrand(AUTOPLAY_ARTICLE_HERO_VIDEOS, vertical);

    const {
      muted = false,
      chromelessAutoplay = false,
    } = autoPlayConfig || {};
    let {
      autoplay = false,
    } = autoPlayConfig || {};

    autoplay = isChromeless ? chromelessAutoplay : autoplay;

    const mainArtAutoPlayCapabilities = {
      canAutoplayMuted: true,
      canAutoplayUnmuted: false,
      isTestingAutoplayCapabilities: false,
    };

    return (
      <div className="article-hero__video">
        <InlineVideo
          loadingLazy={false}
          autoPlay={autoplay}
          autoplayCapabilities={mainArtAutoPlayCapabilities}
          isHero
          onlyAutoplayMuted={muted}
          social={socialMediaProfiles}
          video={primaryMedia.video}
        />
      </div>
    );
  }

  renderLiveVideo = () => {
    const { isVideoPlaying } = this.state;
    const { vertical } = this.props;
    const playmakerKey = get(this.props, 'content.primaryMedia.playmakerLiveVideo.playmakerId');
    const pid = getPIDforPlayerKey(playmakerKey);

    if (pid) {
      const primaryImage = {
        url: {
          primary: get(
            this.props,
            'content.primaryMedia.playmakerLiveVideo.playmakerTeaseImage.url.primary',
            NEWS_NOW_DEFAULT_SLATE,
          ),
        },
      };

      return (
        <div className="article-hero__video">
          <div className="article-hero__video-outer">
            <div className="article-hero__video-inner">
              {isVideoPlaying ? (
                <PlaymakerPlayer
                  loadingLazy={false}
                  autoplayMuted={false}
                  isLive
                  pid={pid}
                  stickyEnabled
                />
              ) : (
                <VideoSlate
                  isLive
                  onPlayClick={() => {
                    this.setState({ isVideoPlaying: true });
                  }}
                  pageView="article"
                  showTeaseImage
                  vertical={vertical}
                  video={{
                    primaryImage,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  renderCaption = () => {
    const {
      content: {
        primaryImage,
        primaryMedia,
      } = {},
      isRecipe,
      isLiveBlog,
    } = this.props;
    const {
      image,
      type,
    } = primaryMedia || {};


    if (!type && !isRecipe) {
      return null;
    }
    const isPlaymaker = type === 'embeddedPlaymakerLiveVideo';

    const captionImage = (isPlaymaker || isRecipe)
      ? primaryImage
      : image;
    return (
      <Caption
        image={captionImage}
        className={classNames(
          'article-hero__caption layout-grid-item',
          {
            'article-hero__caption--playmaker': isPlaymaker,
            'grid-col-10-xl grid-col-push-2-xl': !isLiveBlog,
            'grid-col-6-xl grid-col-push-2-xl': isLiveBlog,
            'article-hero__caption--recipe dn-print': isRecipe,
          },
        )}
      />
    );
  }

  renderSponsor = () => {
    const { content: { nativeAd, source } } = this.props;
    if (!nativeAd || !source) {
      return null;
    }
    const { organization: { externalUrl, primaryImage } = {} } = source;
    return (
      <div className="article-hero__sponsor">
        <Sponsored
          externalUrl={externalUrl}
          primaryImage={primaryImage}
        />
      </div>
    );
  }

  renderTodayLogo = () => {
    // only used for recipe print
    const { isRecipe } = this.props;

    return isRecipe
      ? (
        <div className="dn db-print layout-grid-item">
          <TodayLogo textColor="black" sunriseColor="#FF503C" width="60" height="12" />
        </div>
      )
      : null;
  }

  getDataAttribute = () => {
    // only used for recipe print
    const { content, isRecipe } = this.props;
    if (!isRecipe) return null;

    const { authors } = content || {};

    return authors.map((auth) => auth.name).join(', ');
  }

  render() {
    const {
      content: {
        ecommerceEnabled,
        headline,
        presentation,
        taxonomy,
        unibrow,
      },
      content,
      headlineClassName,
      isLiveBlog,
      stylizedHeader,
      stylizedTag,
      path,
      vertical,
      loadingLazy,
      isRecipe,
    } = this.props;

    if (presentation && presentation.style && presentation.style === 'shell') {
      return null;
    }

    const mediaPresentationStyle = this.getMediaPresentationStyle();
    const { hasStickyVideo } = this.state;
    const isShoppingSection = sectionContainSlug(taxonomy.sections, 'shopping');

    const media = this.getMedia(loadingLazy);

    const containerClasses = classNames(
      'article-hero__container',
      [mediaPresentationStyle],
      {
        'article-hero__shopping-section-container': isShoppingSection,
        'article-hero__live-blog': isLiveBlog,
        'article-hero__sticky-video': hasStickyVideo,
        'recipe-container bg-none-print': isRecipe,
      },
    );

    return (
      <section className={containerClasses} data-test={block}>
        <div
          className={classNames(
            'article-hero__bg-container',
            {
              'article-hero__bg-container--recipe': isRecipe,
            },
          )}
          data-authors={this.getDataAttribute()}
        >
          <header
            className={classNames(
              'article-hero__header',
              'mh0-print',
              'layout-grid-container',
              {
                'article-hero__ecommerce-enabled': ecommerceEnabled || isShoppingSection,
                'article-hero__is-tagged': !!stylizedTag,
                'article-hero__shopping-section': isShoppingSection,
                'pl0-print article-hero__header--recipe': isRecipe,
              },
            )}
            data-test="article-hero__header"
          >
            {stylizedTag}
            <aside
              className={classNames(
                'article-hero__unibrow-grid layout-grid-item grid-col-2-xl',
                {
                  'article-hero__unibrow--recipe': isRecipe,
                },
              )}
            >
              <Unibrow
                size="div"
                className={classNames(
                  'article-hero__tax-term',
                  {
                    'article-hero__ecommerce-enabled': ecommerceEnabled,
                    'article-hero__shopping-section': isShoppingSection,
                    'article-hero__enhanced-opinion': isMSNBCDaily(vertical, taxonomy),
                    'dn-print': isRecipe,
                  },
                )}
                unibrow={unibrow}
              />
            </aside>
            {this.renderTodayLogo()}
            <ArticleHeroHeadline
              className={classNames(
                'article-hero__headline',
                headlineClassName,
                {
                  'article-hero__shopping-section': isShoppingSection,
                  'article-hero__headline--print lh-none-print black-print': isRecipe,
                },
              )}
              dek={this.renderDek()}
              isLiveBlog={isLiveBlog}
              isRecipe={isRecipe}
              isShoppingSection={isShoppingSection}
              path={path}
              vertical={vertical}
              timestamp={this.renderTimestamp()}
            >
              {stylizedHeader}
              {this.renderFeaturedAuthor()}
              {headline.primary}
            </ArticleHeroHeadline>
          </header>
        </div>
        <div className={classNames(
          'article-hero__media-holder layout-grid-container',
          {
            'mb0-print': isRecipe,
          },
        )}
        >
          <RecipeInfo content={content} from="article-hero" />
          {media && (
            <div
              className={classNames(
                'article-hero__media-container layout-grid-item',
                {
                  'grid-col-10-xl grid-col-push-2-xl': !isLiveBlog,
                  'grid-col-7_5-l grid-col-6-xl grid-col-push-2-xl': isLiveBlog,
                },
              )}
            >
              {media}
            </div>
          )}
          {this.renderCaption()}
          {this.renderSponsor()}
        </div>
      </section>
    );
  }
}

export default connect(mapStateToProps)(ArticleHero);
